import { useEffect, useState } from 'react';
import { fetchPublicSurvey, type ShellHydration } from '../../../api.js';
import type { PublicSurvey } from '../types.js';

export type SurveyLoadState = 'loading' | 'ready' | 'error';

export interface SurveyDataResult {
  survey: PublicSurvey | null;
  state: SurveyLoadState;
  errorMessage: string | null;
}

export function useSurveyData(params: {
  hydration: ShellHydration;
  publicId: string;
  prefetched: PublicSurvey | null;
  // When true the host SDK is fetching the payload in parallel; suppress
  // the in-iframe fetch and wait for chirp:survey:payload to update prefetched.
  awaitingPayload?: boolean;
}): SurveyDataResult {
  const [survey, setSurvey] = useState<PublicSurvey | null>(params.prefetched);
  const [state, setState] = useState<SurveyLoadState>(params.prefetched ? 'ready' : 'loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Adopt a late-arriving prefetched payload (from chirp:survey:payload) without
  // refiring our own fetch.
  useEffect(() => {
    if (params.prefetched && !survey) {
      setSurvey(params.prefetched);
      setState('ready');
    }
  }, [params.prefetched, survey]);

  useEffect(() => {
    if (survey) return;
    // SDK promised to deliver via :payload — don't race it from inside the iframe.
    if (params.awaitingPayload) return;
    let cancelled = false;
    void fetchPublicSurvey(params.hydration, params.publicId)
      .then((result) => {
        if (cancelled) return;
        if (result) {
          setSurvey(result as PublicSurvey);
          setState('ready');
        } else {
          setState('error');
          setErrorMessage('Survey not found.');
        }
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setState('error');
        setErrorMessage(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [params.hydration, params.publicId, survey, params.awaitingPayload]);

  return { survey, state, errorMessage };
}
